import {
  Box3,
  type BufferGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Matrix4,
  Mesh,
  MeshLambertMaterial,
  type Object3D,
  Shape,
  ShapeGeometry,
  Vector3,
} from "three";
import type { AirframeKey } from "../aircraft/Airframes";
import type { AircraftTemplate } from "./AssetLoader";

/**
 * Airframe silhouettes.
 *
 * The imported toy plane is one shape; a tower full of identical bodies reads
 * as one aircraft repeated, so the rest of the fleet is built here out of
 * low-poly primitives in the simulation's own frame (+x nose, +y left wing,
 * +z up). Nothing here is loaded, so a silhouette costs a few dozen triangles
 * and no network round trip.
 *
 * Dark parts (engines, cockpits, props) carry the material name the GLB uses
 * for its own dark trim, so `AircraftViewFactory` swaps materials on imported
 * and generated models through exactly one code path.
 */

/** Maps the imported toy plane's axes onto +x forward, +z up. */
const GLB_BASIS = new Matrix4().set(
  0,
  0,
  -1,
  0,
  -1,
  0,
  0,
  0,
  0,
  1,
  0,
  0,
  0,
  0,
  0,
  1,
);

/** Material name the GLB gives its dark trim; reused by the generated parts. */
const DARK_MATERIAL_NAME = "Material_3";

export interface AircraftModelTemplate {
  readonly key: AirframeKey;
  /** Centred on its own origin, ready to be cloned per aircraft. */
  readonly object: Object3D;
  /** Length along the model's forward axis, used to normalise the scale. */
  readonly length: number;
  /** Axis correction, or null when the model is already in the sim's frame. */
  readonly basis: Matrix4 | null;
}

type Triple = readonly [number, number, number];
type Point = readonly [number, number];

interface Part {
  readonly geometry: BufferGeometry;
  readonly position?: Triple;
  readonly rotation?: Triple;
  readonly dark?: boolean;
}

/** Cylinders and cones are born along +y; this points them down the nose. */
const NOSE_ROTATION: Triple = [0, 0, -Math.PI / 2];
/** …and this points them out the tail. */
const TAIL_ROTATION: Triple = [0, 0, Math.PI / 2];
/** Lifts a flat XY profile into the vertical plane, so its +y becomes height. */
const UPRIGHT_ROTATION: Triple = [Math.PI / 2, 0, 0];

function tube(
  length: number,
  front: number,
  back: number = front,
): CylinderGeometry {
  return new CylinderGeometry(front, back, length, 8);
}

function spike(length: number, radius: number): ConeGeometry {
  return new ConeGeometry(radius, length, 8);
}

function disc(radius: number, thickness: number): CylinderGeometry {
  return new CylinderGeometry(radius, radius, thickness, 12);
}

function profileGeometry(outline: readonly Point[]): ShapeGeometry {
  const shape = new Shape();
  shape.moveTo(outline[0]![0], outline[0]![1]);
  for (let i = 1; i < outline.length; i++) {
    shape.lineTo(outline[i]![0], outline[i]![1]);
  }
  shape.closePath();
  return new ShapeGeometry(shape);
}

/**
 * A wing or tailplane from its right half.
 *
 * The half runs root-leading-edge → outboard → root-trailing-edge, and is
 * mirrored into one polygon rather than two mirrored meshes: a mesh scaled by
 * -1 has inverted normals and would shade as the unlit underside.
 */
function planformGeometry(half: readonly Point[]): ShapeGeometry {
  const mirrored = half
    .slice(1, -1)
    .reverse()
    .map(([x, y]): Point => [x, -y]);
  return profileGeometry([...half, ...mirrored]);
}

/** Twin-jet narrowbody: the workhorse silhouette. */
function airlinerParts(): Part[] {
  return [
    { geometry: tube(0.92, 0.075, 0.06), rotation: NOSE_ROTATION },
    {
      geometry: spike(0.17, 0.075),
      position: [0.54, 0, 0],
      rotation: NOSE_ROTATION,
    },
    {
      geometry: spike(0.24, 0.06),
      position: [-0.58, 0, 0.02],
      rotation: TAIL_ROTATION,
    },
    {
      geometry: tube(0.1, 0.078),
      position: [0.4, 0, 0.01],
      rotation: NOSE_ROTATION,
      dark: true,
    },
    {
      geometry: planformGeometry([
        [0.08, 0.02],
        [-0.1, 0.32],
        [-0.16, 0.54],
        [-0.28, 0.55],
        [-0.22, 0.32],
        [-0.16, 0.02],
      ]),
      position: [0, 0, -0.02],
    },
    {
      geometry: planformGeometry([
        [-0.4, 0.02],
        [-0.5, 0.2],
        [-0.6, 0.21],
        [-0.56, 0.03],
      ]),
      position: [0, 0, 0.03],
    },
    {
      geometry: profileGeometry([
        [-0.4, 0.03],
        [-0.54, 0.3],
        [-0.63, 0.3],
        [-0.63, 0.03],
      ]),
      rotation: UPRIGHT_ROTATION,
    },
    {
      geometry: tube(0.22, 0.05, 0.045),
      position: [-0.02, 0.3, -0.08],
      rotation: NOSE_ROTATION,
      dark: true,
    },
    {
      geometry: tube(0.22, 0.05, 0.045),
      position: [-0.02, -0.3, -0.08],
      rotation: NOSE_ROTATION,
      dark: true,
    },
  ];
}

/** Four-engine widebody: longer, fatter, and a visibly bigger span. */
function heavyParts(): Part[] {
  const nacelle = (y: number): Part => ({
    geometry: tube(0.24, 0.056, 0.05),
    position: [-0.02, y, -0.09],
    rotation: NOSE_ROTATION,
    dark: true,
  });
  return [
    { geometry: tube(1.02, 0.1, 0.075), rotation: NOSE_ROTATION },
    {
      geometry: spike(0.18, 0.1),
      position: [0.6, 0, 0],
      rotation: NOSE_ROTATION,
    },
    {
      geometry: spike(0.26, 0.075),
      position: [-0.64, 0, 0.03],
      rotation: TAIL_ROTATION,
    },
    {
      geometry: tube(0.12, 0.103),
      position: [0.44, 0, 0.01],
      rotation: NOSE_ROTATION,
      dark: true,
    },
    {
      geometry: planformGeometry([
        [0.1, 0.03],
        [-0.12, 0.4],
        [-0.2, 0.7],
        [-0.34, 0.71],
        [-0.26, 0.4],
        [-0.2, 0.03],
      ]),
      position: [0, 0, -0.03],
    },
    {
      geometry: planformGeometry([
        [-0.44, 0.03],
        [-0.56, 0.26],
        [-0.68, 0.27],
        [-0.62, 0.04],
      ]),
      position: [0, 0, 0.04],
    },
    {
      geometry: profileGeometry([
        [-0.44, 0.04],
        [-0.6, 0.38],
        [-0.7, 0.38],
        [-0.7, 0.04],
      ]),
      rotation: UPRIGHT_ROTATION,
    },
    nacelle(0.24),
    nacelle(-0.24),
    nacelle(0.46),
    nacelle(-0.46),
  ];
}

/** High-wing turboprop: stubby, with two prop discs out front. */
function turbopropParts(): Part[] {
  const engine = (y: number): Part[] => [
    {
      geometry: tube(0.26, 0.05),
      position: [0.06, y, 0.04],
      rotation: NOSE_ROTATION,
      dark: true,
    },
    {
      geometry: disc(0.15, 0.012),
      position: [0.2, y, 0.04],
      rotation: NOSE_ROTATION,
      dark: true,
    },
  ];
  return [
    { geometry: tube(0.78, 0.095, 0.055), rotation: NOSE_ROTATION },
    {
      geometry: spike(0.14, 0.095),
      position: [0.46, 0, 0],
      rotation: NOSE_ROTATION,
    },
    {
      geometry: spike(0.22, 0.055),
      position: [-0.5, 0, 0.05],
      rotation: TAIL_ROTATION,
    },
    {
      geometry: tube(0.1, 0.098),
      position: [0.34, 0, 0.01],
      rotation: NOSE_ROTATION,
      dark: true,
    },
    {
      // Straight, untapered, and mounted on the spine — the shape that says
      // "turboprop" at a glance even at this pixel size.
      geometry: planformGeometry([
        [0.12, 0.03],
        [0.12, 0.5],
        [-0.08, 0.5],
        [-0.08, 0.03],
      ]),
      position: [0, 0, 0.08],
    },
    ...engine(0.28),
    ...engine(-0.28),
    {
      geometry: planformGeometry([
        [-0.34, 0.02],
        [-0.4, 0.2],
        [-0.5, 0.2],
        [-0.48, 0.03],
      ]),
      position: [0, 0, 0.05],
    },
    {
      geometry: profileGeometry([
        [-0.32, 0.04],
        [-0.44, 0.34],
        [-0.54, 0.34],
        [-0.54, 0.04],
      ]),
      rotation: UPRIGHT_ROTATION,
    },
  ];
}

/** Delta-wing fast mover: long nose, twin fins, twin exhausts. */
function jetParts(): Part[] {
  const fin = (y: number): Part => ({
    geometry: profileGeometry([
      [-0.3, 0.03],
      [-0.44, 0.26],
      [-0.52, 0.26],
      [-0.5, 0.03],
    ]),
    position: [0, y, 0],
    rotation: UPRIGHT_ROTATION,
  });
  const exhaust = (y: number): Part => ({
    geometry: tube(0.16, 0.045),
    position: [-0.46, y, 0],
    rotation: NOSE_ROTATION,
    dark: true,
  });
  return [
    { geometry: tube(0.86, 0.06, 0.07), rotation: NOSE_ROTATION },
    {
      geometry: spike(0.3, 0.06),
      position: [0.58, 0, 0],
      rotation: NOSE_ROTATION,
    },
    {
      geometry: tube(0.12, 0.064),
      position: [0.3, 0, 0.02],
      rotation: NOSE_ROTATION,
      dark: true,
    },
    {
      geometry: planformGeometry([
        [0.14, 0.02],
        [-0.34, 0.36],
        [-0.42, 0.36],
        [-0.34, 0.02],
      ]),
      position: [0, 0, -0.01],
    },
    {
      // Canards: a second, much smaller pair up front reads as "fast" even
      // when the aircraft is only a few pixels across.
      geometry: planformGeometry([
        [0.3, 0.02],
        [0.2, 0.16],
        [0.14, 0.16],
        [0.2, 0.02],
      ]),
      position: [0, 0, 0.01],
    },
    fin(0.1),
    fin(-0.1),
    exhaust(0.06),
    exhaust(-0.06),
  ];
}

/** Business jet: slim, rear-mounted engines, T-tail. */
function bizjetParts(): Part[] {
  const engine = (y: number): Part => ({
    geometry: tube(0.2, 0.048, 0.042),
    position: [-0.26, y, 0.04],
    rotation: NOSE_ROTATION,
    dark: true,
  });
  return [
    { geometry: tube(0.8, 0.062, 0.05), rotation: NOSE_ROTATION },
    {
      geometry: spike(0.16, 0.062),
      position: [0.48, 0, 0],
      rotation: NOSE_ROTATION,
    },
    {
      geometry: spike(0.2, 0.05),
      position: [-0.5, 0, 0.02],
      rotation: TAIL_ROTATION,
    },
    {
      geometry: tube(0.09, 0.065),
      position: [0.35, 0, 0.01],
      rotation: NOSE_ROTATION,
      dark: true,
    },
    {
      geometry: planformGeometry([
        [0.04, 0.02],
        [-0.14, 0.28],
        [-0.22, 0.44],
        [-0.3, 0.44],
        [-0.24, 0.28],
        [-0.16, 0.02],
      ]),
      position: [0, 0, -0.02],
    },
    engine(0.12),
    engine(-0.12),
    {
      geometry: profileGeometry([
        [-0.36, 0.04],
        [-0.5, 0.32],
        [-0.58, 0.32],
        [-0.56, 0.04],
      ]),
      rotation: UPRIGHT_ROTATION,
    },
    {
      // The T-tail's tailplane sits on top of the fin, not on the fuselage.
      geometry: planformGeometry([
        [-0.46, 0.02],
        [-0.52, 0.18],
        [-0.6, 0.18],
        [-0.58, 0.03],
      ]),
      position: [0, 0, 0.32],
    },
  ];
}

const PART_BUILDERS: Readonly<
  Record<Exclude<AirframeKey, "toy">, () => Part[]>
> = Object.freeze({
  airliner: airlinerParts,
  heavy: heavyParts,
  turboprop: turbopropParts,
  jet: jetParts,
  bizjet: bizjetParts,
});

/**
 * Owns the generated geometry and the placeholder materials it is built with.
 *
 * The per-aircraft materials are cloned by the view factory; these two only
 * carry the light/dark distinction through to it, and are disposed with the
 * library.
 */
export class AircraftModelLibrary {
  private readonly bodyMaterial = new MeshLambertMaterial({
    name: "silhouette-body",
  });
  private readonly darkMaterial = new MeshLambertMaterial({
    name: DARK_MATERIAL_NAME,
  });
  private readonly geometries: BufferGeometry[] = [];
  private readonly templates = new Map<AirframeKey, AircraftModelTemplate>();

  constructor(toy?: AircraftTemplate) {
    if (toy) {
      this.templates.set("toy", {
        key: "toy",
        object: toy.template,
        length: toy.templateLength,
        basis: GLB_BASIS,
      });
    }
    for (const key of Object.keys(PART_BUILDERS) as Exclude<
      AirframeKey,
      "toy"
    >[]) {
      this.templates.set(key, this.build(key, PART_BUILDERS[key]()));
    }
  }

  /** Falls back to the airliner, so a missing toy model costs variety only. */
  templateFor(key: AirframeKey): AircraftModelTemplate {
    return this.templates.get(key) ?? this.templates.get("airliner")!;
  }

  get keys(): readonly AirframeKey[] {
    return [...this.templates.keys()];
  }

  private build(
    key: AirframeKey,
    parts: readonly Part[],
  ): AircraftModelTemplate {
    const group = new Group();
    group.name = `aircraft-${key}`;
    for (const part of parts) {
      this.geometries.push(part.geometry);
      const mesh = new Mesh(
        part.geometry,
        part.dark ? this.darkMaterial : this.bodyMaterial,
      );
      if (part.position) {
        mesh.position.set(...part.position);
      }
      if (part.rotation) {
        mesh.rotation.set(...part.rotation);
      }
      group.add(mesh);
    }

    group.updateMatrixWorld(true);
    const box = new Box3().setFromObject(group);
    const centre = box.getCenter(new Vector3());
    const size = box.getSize(new Vector3());
    group.position.sub(centre);
    group.updateMatrixWorld(true);

    return {
      key,
      object: group,
      length: Math.max(size.x, 0.001),
      basis: null,
    };
  }

  dispose(): void {
    for (const geometry of this.geometries) {
      geometry.dispose();
    }
    this.geometries.length = 0;
    this.templates.clear();
    this.bodyMaterial.dispose();
    this.darkMaterial.dispose();
  }
}
